import { useNavigate, Link } from "react-router-dom";
import { useState } from "react";
import { toast } from "sonner";
import { Building2, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SsoButtons } from "@/components/parcelio/sso-buttons";
import { authApi, ApiError } from "@/lib/api";

type AccountType = "individual" | "company";

const COMPANY_TYPES = [
  { value: "sole_proprietorship", label: "Sole proprietorship" },
  { value: "ltd", label: "Limited (LTD)" },
  { value: "plc", label: "Public limited company (PLC)" },
  { value: "startup", label: "Startup" },
];

const siteUrl = (import.meta.env.VITE_SITE_URL as string | undefined) ?? "";

export default function RegisterPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [accountType, setAccountType] = useState<AccountType>("individual");

  return (
    <div>
      <h1 className="text-2xl font-bold tracking-tight">Create your account</h1>
      <p className="mt-1 text-sm text-muted-foreground">
        Start with the free operator tier. No card required.
      </p>

      <div className="mt-6">
        <SsoButtons mode="register" />
      </div>
      <div className="my-6 flex items-center gap-3">
        <div className="h-px flex-1 bg-border" />
        <span className="text-xs uppercase tracking-wider text-muted-foreground">or with email</span>
        <div className="h-px flex-1 bg-border" />
      </div>

      <form
        className="space-y-4"
        onSubmit={async (e) => {
          e.preventDefault();
          setLoading(true);
          const formData = new FormData(e.currentTarget);
          const email = formData.get("email") as string;
          const first = formData.get("first") as string;
          const last = formData.get("last") as string;
          try {
            await authApi.register({
              full_name: `${first} ${last}`.trim(),
              email,
              password: formData.get("password") as string,
              account_type: accountType,
              office_address: (formData.get("officeAddress") as string) || undefined,
              registration_no: (formData.get("registrationNo") as string) || undefined,
              company_type: (formData.get("companyType") as string) || undefined,
              tin: (formData.get("taxId") as string) || undefined,
              company_phone: (formData.get("contactPhone") as string) || undefined,
            });
            toast.success("Account created — check your inbox to confirm");
            navigate(`/auth/verify-email?email=${encodeURIComponent(email)}`);
          } catch (err) {
            toast.error(err instanceof ApiError ? err.message : "Could not create account");
          } finally {
            setLoading(false);
          }
        }}
      >
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label htmlFor="first">First name</Label>
            <Input id="first" name="first" required autoComplete="given-name" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="last">Last name</Label>
            <Input id="last" name="last" required autoComplete="family-name" />
          </div>
        </div>
        <div className="space-y-2">
          <Label>Account type</Label>
          <RadioGroup
            value={accountType}
            onValueChange={(v) => setAccountType(v as AccountType)}
            className="grid grid-cols-2 gap-3"
          >
            <label
              htmlFor="type-individual"
              className={`flex cursor-pointer items-center gap-2.5 rounded-lg border-2 px-3 py-2.5 text-sm font-medium transition-colors ${
                accountType === "individual" ? "border-primary bg-primary-soft text-primary" : "border-border text-muted-foreground"
              }`}
            >
              <RadioGroupItem value="individual" id="type-individual" />
              <User className="h-4 w-4" />
              Individual
            </label>
            <label
              htmlFor="type-company"
              className={`flex cursor-pointer items-center gap-2.5 rounded-lg border-2 px-3 py-2.5 text-sm font-medium transition-colors ${
                accountType === "company" ? "border-primary bg-primary-soft text-primary" : "border-border text-muted-foreground"
              }`}
            >
              <RadioGroupItem value="company" id="type-company" />
              <Building2 className="h-4 w-4" />
              Company
            </label>
          </RadioGroup>
        </div>
        <div className="space-y-2">
          <Label htmlFor="company">{accountType === "company" ? "Company name" : "Company (optional)"}</Label>
          <Input id="company" name="company" required={accountType === "company"} placeholder="Acme Logistics" />
        </div>
        {accountType === "company" ? (
          <div className="space-y-4 rounded-lg border border-dashed border-border p-4">
            <div className="space-y-2">
              <Label htmlFor="officeAddress">Office address</Label>
              <Input id="officeAddress" name="officeAddress" required placeholder="12 Marina Road, Lagos" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="registrationNo">Registration no.</Label>
                <Input id="registrationNo" name="registrationNo" required placeholder="RC1234567" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="companyType">Company type</Label>
                <Select name="companyType" required>
                  <SelectTrigger id="companyType" className="w-full">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {COMPANY_TYPES.map((t) => (
                      <SelectItem key={t.value} value={t.value}>
                        {t.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="taxId">Tax identification no. (TIN)</Label>
              <Input id="taxId" name="taxId" placeholder="Optional" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="contactPhone">Company phone</Label>
              <Input id="contactPhone" name="contactPhone" type="tel" required autoComplete="tel" placeholder="+234 800 000 0000" />
            </div>
          </div>
        ) : null}
        <div className="space-y-2">
          <Label htmlFor="email">Work email</Label>
          <Input id="email" name="email" type="email" required autoComplete="email" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="password">Password</Label>
          <Input id="password" name="password" type="password" required autoComplete="new-password" />
          <p className="text-xs text-muted-foreground">Minimum 8 characters, one number, one symbol.</p>
        </div>
        <label className="flex items-start gap-2 text-sm text-muted-foreground">
          <Checkbox id="terms" required className="mt-0.5" />
          <span>
            I agree to the{" "}
            <a href={`${siteUrl}/terms`} target="_blank" rel="noreferrer" className="text-foreground underline">Terms</a> and{" "}
            <a href={`${siteUrl}/privacy`} target="_blank" rel="noreferrer" className="text-foreground underline">Privacy Policy</a>.
          </span>
        </label>
        <Button type="submit" className="w-full" disabled={loading}>
          {loading ? "Creating account…" : "Create account"}
        </Button>
      </form>

      <p className="mt-6 text-center text-sm text-muted-foreground">
        Have an account?{" "}
        <Link to="/auth/login" className="font-medium text-foreground hover:underline">
          Sign in
        </Link>
      </p>
    </div>
  );
}
